// --- React
import React, { useCallback } from "react";
// --- Actions & Hooks
import { useDialogsActions } from "../hooks";
import { useDialogsForm } from "../forms";
// --- External UI Libraries
import Typography from "@dtci/spellbook/Typography";

/** * DeleteForm Component
 *
 * This component is responsible for rendering the delete confirmation message.
 * It displays a message to confirm the deletion action.
 *
 * @component
 * @param {Object} msg - The message object containing confirmation text.
 * @returns {JSX.Element} The rendered ConfirmationForm component.
 */
const ConfirmationForm = ({ msg }) => (
  <div className="flexBox">
    <Typography variant="T30">{msg[0]}</Typography>
    <Typography variant="T20">{msg[1]}</Typography>
  </div>
);

/**
 * useConfirmationDialog Hook
 *
 * This hook provides functionality to open a confirmation dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openConfirmationDialog function.
 */
export const useConfirmationDialog = () => {
  const { openDialog, closeDialog } = useDialogsActions();
  const form = useDialogsForm();

  const handleSubmit = useCallback(
    (onSubmit) => {
      onSubmit(form.state.formData);
      form.reset();
      closeDialog();
    },
    [form, closeDialog],
  );

  const openConfirmationDialog = useCallback(
    (title, label, msg, onConfirm) => {
      openDialog({
        title: title,
        content: <ConfirmationForm msg={msg} />,
        closeAction: true,
        primaryAction: {
          label,
          action: () => handleSubmit(onConfirm),
        },
      });
    },
    [openDialog, handleSubmit],
  );

  return { openConfirmationDialog };
};
